import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import api from "@/lib/api";
import { LayoutDashboard, Briefcase, Search, LogOut, Timer as TimerIcon, FileText, Settings as SettingsIcon, FileBarChart, Users, Upload, HandCoins, Receipt, Calendar, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import TimerWidget from "@/components/TimerWidget";
import BrandMark from "@/components/BrandMark";
import { getTimer, subscribeTimer } from "@/lib/timer";

const STATUS_BADGE = {
  Active: "bg-green-50 text-green-700 border-green-200",
  Pending: "bg-slate-100 text-slate-600 border-slate-200",
  Closed: "bg-slate-100 text-slate-500 border-slate-200",
};

function GlobalSearch() {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onChange = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (!val.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: val } });
        setResults(data.results);
        setOpen(true);
      } catch (e) {
        /* ignore */
      }
    }, 250);
  };

  const pick = (m) => {
    setOpen(false);
    setQ("");
    setResults([]);
    navigate(`/matters/${m.id}`);
  };

  return (
    <div ref={boxRef} className="relative flex-1 min-w-0 max-w-xl">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={t("search.placeholder")}
        className="w-full pl-9 pr-4 h-10 rounded-md border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-transparent"
      />
      {open && (
        <div
          data-testid="global-search-results"
          className="absolute mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-96 overflow-auto"
        >
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500" data-testid="global-search-empty">
              {t("search.empty", { q })}
            </div>
          ) : (
            results.map((m) => (
              <button
                key={m.id}
                data-testid={`search-result-${m.internal_ref || m.id}`}
                onClick={() => pick(m)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-800 truncate">{m.name}</span>
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${STATUS_BADGE[m.status] || ""}`}>
                    {m.status}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {m.client}
                  {m.internal_ref ? ` · Ref ${m.internal_ref}` : ""}
                  {m.court_case_number ? ` · Case ${m.court_case_number}` : ""}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const NAV_ITEMS = [
  { to: "/", key: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/matters", key: "nav.matters", icon: Briefcase, testid: "nav-matters" },
  { to: "/timer", key: "nav.timer", icon: TimerIcon, testid: "nav-timer" },
  { to: "/calendar", key: "nav.calendar", icon: Calendar, testid: "nav-calendar" },
  { to: "/invoices", key: "nav.invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/receivables", key: "nav.receivables", icon: HandCoins, testid: "nav-receivables" },
  { to: "/receipts", key: "nav.receipts", icon: Receipt, testid: "nav-receipts" },
  { to: "/reports", key: "nav.reports", icon: FileBarChart, testid: "nav-reports" },
  { to: "/clients", key: "nav.clients", icon: Users, testid: "nav-clients" },
  { to: "/import", key: "nav.import", icon: Upload, testid: "nav-import" },
  { to: "/settings", key: "nav.settings", icon: SettingsIcon, testid: "nav-settings" },
];

function NavBody({ testidPrefix = "", onNavigate, user, doLogout }) {
  const { t } = useT();
  const navCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${
      isActive ? "bg-slate-800 text-white font-semibold" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
    }`;
  return (
    <>
      <div className="px-5 py-6 border-b border-slate-800">
        <div className="flex items-center gap-2.5">
          <BrandMark className="h-10 w-10" iconClassName="h-6 w-6 text-amber-500" />
          <div>
            <div className="font-serif text-lg font-semibold text-white leading-tight">RPLAW</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Case &amp; Deadline Manager</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV_ITEMS.map(({ to, key, icon: Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={navCls}
            data-testid={`${testidPrefix}${testid}`}
            onClick={onNavigate}
          >
            <Icon className="h-4 w-4" /> {t(key)}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-slate-800">
        <div className="text-xs text-slate-400 truncate" data-testid={`${testidPrefix}sidebar-user-email`}>
          {user?.email}
        </div>
        <button
          data-testid={`${testidPrefix}logout-button`}
          onClick={doLogout}
          className="mt-2 flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" /> {t("nav.sign_out")}
        </button>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, changeLanguage } = useT();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [timerActive, setTimerActive] = useState(Boolean(getTimer()));

  useEffect(() => subscribeTimer(() => setTimerActive(Boolean(getTimer()))), []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      <aside className="fixed inset-y-0 left-0 w-60 bg-slate-900 hidden md:flex flex-col z-40">
        <NavBody user={user} doLogout={doLogout} />
      </aside>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="left"
          className="w-72 max-w-[85vw] bg-slate-900 border-slate-800 p-0 text-slate-300 flex flex-col"
          data-testid="mobile-nav-drawer"
        >
          <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
          <NavBody testidPrefix="m-" onNavigate={() => setMenuOpen(false)} user={user} doLogout={doLogout} />
        </SheetContent>
      </Sheet>

      <div className="md:pl-60">
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <button
            data-testid="nav-menu-button"
            onClick={() => setMenuOpen(true)}
            aria-label={t("nav.menu")}
            className="md:hidden shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-1 border border-slate-200 rounded-md p-0.5 bg-white shrink-0" data-testid="language-switcher">
            {["lt", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => changeLanguage(l)}
                className={`px-2.5 py-1 text-xs font-bold rounded transition-colors ${
                  lang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </header>
        <main className={`px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl w-full min-w-0 ${timerActive ? "pb-32 md:pb-8" : ""}`}>
          <Outlet />
        </main>
        <TimerWidget />
      </div>
    </div>
  );
}
